import { getCookie, toggleConsent } from "@providers";
import { getUser } from "@utils";

const LANGUAGE_ALIASES = {
	br: ["pt"],
	pt: ["br"],
};

const normalizeLanguageCode = (languageCode) =>
	(languageCode || localStorage.getItem("lang") || "en").toLowerCase();

const getLanguageFallbacks = (languageCode) => {
	const normalizedLanguage = normalizeLanguageCode(languageCode);
	const aliases = LANGUAGE_ALIASES[normalizedLanguage] ?? [];

	return [...new Set([normalizedLanguage, ...aliases, "en"])];
};

export const getCategoryLocalizedName = (category, languageCode = "en") => {
	const translations = category?.local ?? category?.localize ?? category?.locals;

	if (!translations) {
		return category?.name ?? "";
	}

	if (Array.isArray(translations)) {
		for (const fallbackLanguage of getLanguageFallbacks(languageCode)) {
			const match = translations.find(
				(item) =>
					item?.locale === fallbackLanguage ||
					item?.language === fallbackLanguage ||
					item?.lang === fallbackLanguage ||
					item?.code === fallbackLanguage
			);

			if (match) {
				return match?.name ?? match?.value ?? match?.label ?? category?.name ?? "";
			}
		}

		return category?.name ?? "";
	}

	if (typeof translations === "object") {
		for (const fallbackLanguage of getLanguageFallbacks(languageCode)) {
			const directMatch = translations[fallbackLanguage];
			if (typeof directMatch === "string") {
				return directMatch;
			}
		}

		for (const value of Object.values(translations)) {
			if (!value || typeof value !== "object" || Array.isArray(value)) {
				continue;
			}

			for (const fallbackLanguage of getLanguageFallbacks(languageCode)) {
				const nestedMatch = value[fallbackLanguage];
				if (typeof nestedMatch === "string") {
					return nestedMatch;
				}
			}
		}

		const flatString = Object.values(translations).find(
			(value) => typeof value === "string"
		);
		if (typeof flatString === "string") {
			return flatString;
		}
	}

	return category?.name ?? "";
};

export const matchesCategoryLiveFilter = (category, isLive) => {
	if (isLive === null) {
		return true;
	}

	if (isLive === true) {
		return category?.isLive === true;
	}

	return category?.isLive !== true;
};

export const getTournamentOptions = () => [
	{
		value: "DROP AND WINS | CASINO",
		key: "drop-and-wins-casino",
	},
	{
		value: "DROP AND WINS | CANLI CASINO",
		key: "drop-and-wins-live-casino",
	},
];

export const getSortOptions = () => [
	{
		value: "casino.most-popular-games",
		key: "most-popular-games",
	},
	{
		value: "casino.asc",
		key: "asc",
		translate: "casino.asc",
	},
	{
		value: "casino.desc",
		key: "desc",
	},
	{
		value: "casino.random",
		key: "random",
	},
];

export const openGame = (game, goToLink, showAlert, params, type) => {
	let user = getUser();

	if (user) {
		const cookieConsent = getCookie();

		if (!cookieConsent) {
			showAlert({
				type: "error",
				msg: "COOKIE_CONSENT_REQUIRED",
				title: "ERROR",
				callback: () => {
					toggleConsent(true);
				},
			});
			return;
		}

		const gameId = game.game_id || game.gameId;
		const queryParams = `gameId=${gameId}&provider=${game.provider}`;

		goToLink({
			pathname: "/casino/game",
			search: `?${queryParams}`,
			state: {
				id: game.id,
				hasLobby: game.hasLobby,
				technology: game.technology,
				affiliate: game.affiliate,
				gameId: gameId,
				lang: "en-US",
				practice: 0,
				provider: game.provider,
				localGameType: game.remote_game_category,
				is_live: game.is_live,
				tableName: game.table_name,
				tableId: game.table_id || game.tableId,
				preParams: params,
			},
		});
		if (type === "gameplay") {
			window.location.reload();
		}
	} else {
		showAlert({
			type: "error",
			msg: "LOGIN_OR_SIGNUP_FIRST",
			title: "ERROR",
		});
	}
};
